import React, { useState } from "react";
import { Form, Col, Row, Button, Container, InputGroup } from "react-bootstrap";

// Helper function to convert to camelCase (e.g., "Drawing Status" -> "drawingStatus")
const toCamelCase = (str) => {
  return str
    .replace(/\s(.)/g, (match, group1) => group1.toUpperCase())
    .replace(/\s/g, "")
    .replace(/^(.)/, (group1) => group1.toLowerCase());
};

// Helper function to convert to PascalCase (e.g., "Drawing Status" -> "DrawingStatus")
const toPascalCase = (str) => {
  return str
    .replace(/\s(.)/g, (match, group1) => group1.toUpperCase())
    .replace(/\s/g, "")
    .replace(/^(.)/, (group1) => group1.toUpperCase());
};

// Helper function to format field labels (e.g., "Drawing Status" -> "Drawing Status")
const formatLabel = (str) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

// Helper function to generate the JSX code as a string
const generateFormFieldCode = (fieldName, inputType, labelPosition) => {
  if (!fieldName) {
    return { jsx: "<Col />", options: "" };
  }

  const camelCaseFieldName = toCamelCase(fieldName);
  const pascalCaseFieldName = toPascalCase(fieldName);
  const formattedLabel = formatLabel(fieldName);

  if (inputType === "text") {
    if (labelPosition === "next") {
      return {
        jsx: `
  <Col>
    <Form.Group as={Row} controlId="${camelCaseFieldName}">
      <Form.Label column sm={3}>
        ${formattedLabel}
      </Form.Label>
      <Col >
        <Form.Control
          type="text"
          name="${camelCaseFieldName}"
          value={formData.${camelCaseFieldName} || ""} 
          onFocus={handleFocus}
          onBlur={(e) =>
            handleBlur(
              e,
              "${formattedLabel}",
              [], 
              {
                ID: formData.ID,
                url: apiUrl,
                field: "${pascalCaseFieldName}",
                tableKey,
              }
            )
          }
          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
          className={zoneTbdFields?.${camelCaseFieldName} ? "tbd-highlight" : ""}
          onChange={handleChange}
          placeholder="Enter ${formattedLabel}"
        />
      </Col>
    </Form.Group>
  </Col>
`,
        options: "",
      };
    } else {
      return {
        jsx: `
  <Col>
    <Form.Group controlId="${camelCaseFieldName}">
      <Form.Label>${formattedLabel}</Form.Label>
      <Form.Control
        type="text"
        name="${camelCaseFieldName}"
        value={formData.${camelCaseFieldName} || ""}
        onFocus={handleFocus}
        onBlur={(e) =>
          handleBlur(
            e,
            "${formattedLabel}",
            [], 
            {
              ID: formData.ID,
              url: apiUrl,
              field: "${pascalCaseFieldName}",
              tableKey,
            }
          )
        }
        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
        className={zoneTbdFields?.${camelCaseFieldName} ? "tbd-highlight" : ""}
        onChange={handleChange}
        placeholder="Enter ${formattedLabel}"
      />
    </Form.Group>
  </Col>
`,
        options: "",
      };
    }
  }

  // Handle dropdowns (regular and generic)
  if (inputType === "dropdown" || inputType === "genericDropdown") {
    const dropdownOptionsVariable =
      inputType === "genericDropdown"
        ? `genericOptions`
        : `${camelCaseFieldName}Options`;

    const dropdownFilter =
      inputType === "genericDropdown"
        ? `"Generic Yes/ No"`
        : `"${formattedLabel}"`;

    const dropdownJsx =
      labelPosition === "next"
        ? `
  <Col>
    <Form.Group as={Row} controlId="${camelCaseFieldName}">
      <Form.Label column sm={3}>
        ${formattedLabel}
      </Form.Label>
      <Col>
        <Form.Select
          name="${camelCaseFieldName}"
          value={formData.${camelCaseFieldName} || ""}
          onFocus={handleFocus}
          onBlur={(e) =>
            handleBlur(
              e,
              "${formattedLabel}",
              ${dropdownOptionsVariable},
              {
                ID: formData.ID,
                url: apiUrl,
                field: "${pascalCaseFieldName}",
                tableKey,
              }
            )
          }
          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
          className={zoneTbdFields?.${camelCaseFieldName} ? "tbd-highlight" : ""}
          onChange={handleChange}
        >
          <option value="">Select ${formattedLabel}</option>
          {${dropdownOptionsVariable}.map((option) => (
            <option key={option.ID} value={option.DisplayText}>
              {option.DisplayText}
            </option>
          ))}
        </Form.Select>
      </Col>
    </Form.Group>
  </Col>
`
        : `
  <Col>
    <Form.Group controlId="${camelCaseFieldName}">
      <Form.Label>${formattedLabel}</Form.Label>
      <Form.Select
        name="${camelCaseFieldName}"
        value={formData.${camelCaseFieldName} || ""}
        onFocus={handleFocus}
        onBlur={(e) =>
          handleBlur(
            e,
            "${formattedLabel}",
            ${dropdownOptionsVariable},
            {
              ID: formData.ID,
              url: apiUrl,
              field: "${pascalCaseFieldName}",
              tableKey,
            }
          )
        }
        disabled={isDisabled || (!roles.Admin && !roles.Apps)}
        className={zoneTbdFields?.${camelCaseFieldName} ? "tbd-highlight" : ""}
        onChange={handleChange}
      >
        <option value="">Select ${formattedLabel}</option>
        {${dropdownOptionsVariable}.map((option) => (
          <option key={option.ID} value={option.DisplayText}>
            {option.DisplayText}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  </Col>`;

    return {
      jsx: dropdownJsx,
      options: `const ${dropdownOptionsVariable} =
        options?.filter((option) => option.DropdownType === ${dropdownFilter})
        .sort((a, b) => a.SortOrder - b.SortOrder) || [];`,
    };
  }

  return { jsx: "", options: "" };
};

const FormFieldBuilder = () => {
  const [rows, setRows] = useState([
    { fields: [{ fieldName: "", inputType: "text", labelPosition: "above" }] },
  ]);
  const [generatedCode, setGeneratedCode] = useState("");
  const [generatedDropdownOptions, setGeneratedDropdownOptions] = useState("");

  // Handle input changes for a specific field
  const handleFieldChange = (rowIndex, fieldIndex, field, value) => {
    const updatedRows = rows.map((row, rIndex) =>
      rIndex === rowIndex
        ? {
            ...row,
            fields: row.fields.map((f, fIndex) =>
              fIndex === fieldIndex ? { ...f, [field]: value } : f
            ),
          }
        : row
    );
    setRows(updatedRows);
  };

  // Add a new row of input fields
  const addFieldRow = (rowIndex) => {
    const updatedRows = rows.map((row, rIndex) =>
      rIndex === rowIndex
        ? {
            ...row,
            fields: [
              ...row.fields,
              { fieldName: "", inputType: "text", labelPosition: "above" },
            ],
          }
        : row
    );
    setRows(updatedRows);
  };

  // Add a new row
  const addNewRow = () => {
    setRows([
      ...rows,
      {
        fields: [{ fieldName: "", inputType: "text", labelPosition: "above" }],
      },
    ]);
  };

  // Remove a specific row of input fields
  const removeFieldRow = (rowIndex, fieldIndex) => {
    const updatedRows = rows.map((row, rIndex) =>
      rIndex === rowIndex
        ? {
            ...row,
            fields: row.fields.filter((_, fIndex) => fIndex !== fieldIndex),
          }
        : row
    );
    setRows(updatedRows);
  };

  // Remove a specific row
  const removeRow = (rowIndex) => {
    const updatedRows = rows.filter((_, rIndex) => rIndex !== rowIndex);
    setRows(updatedRows);
  };

// Generate code for all rows and fields
const handleGenerateCode = () => {
  const allCode = rows.reduce(
    (acc, row) => {
      const rowCode = row.fields.reduce(
        (rowAcc, { fieldName, inputType, labelPosition }) => {
          const codeObj = generateFormFieldCode(fieldName, inputType, labelPosition);
          
          // Accumulate JSX
          rowAcc.jsx += "\n" + codeObj.jsx;
  
          // Accumulate options if available
          if (codeObj.options) {
            rowAcc.options += `\n${codeObj.options}`;
          }
  
          return rowAcc;
        },
        { jsx: "", options: "" }
      );
  
      acc.jsx += `\n<Row className="mb-3">${rowCode.jsx}</Row>`;
      acc.options += rowCode.options;
  
      return acc;
    },
    { jsx: "", options: "" }
  );

  // Set the JSX and options in separate states
  setGeneratedCode(allCode.jsx.trim());
  setGeneratedDropdownOptions(allCode.options.trim());
};

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Container>
      <div>
        <h3>Generate Form Field</h3>
        <Form>
          {rows.map((row, rowIndex) => (
            <div key={rowIndex} className="mb-4">
              <h4>Row {rowIndex + 1}</h4>
              {row.fields.map((field, fieldIndex) => (
                <Row className="align-items-center" key={fieldIndex}>
                  <Col xs={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Field Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter field name"
                        value={field.fieldName}
                        onChange={(e) =>
                          handleFieldChange(
                            rowIndex,
                            fieldIndex,
                            "fieldName",
                            e.target.value
                          )
                        }
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Input Type</Form.Label>
                      <Form.Select
                        value={field.inputType}
                        onChange={(e) =>
                          handleFieldChange(
                            rowIndex,
                            fieldIndex,
                            "inputType",
                            e.target.value
                          )
                        }
                      >
                        <option value="text">Text Input</option>
                        <option value="dropdown">Dropdown</option>
                        <option value="genericDropdown">
                          Generic Dropdown
                        </option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xs={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Label Position</Form.Label>
                      <Form.Select
                        value={field.labelPosition}
                        onChange={(e) =>
                          handleFieldChange(
                            rowIndex,
                            fieldIndex,
                            "labelPosition",
                            e.target.value
                          )
                        }
                      >
                        <option value="above">Label Above</option>
                        <option value="next">Label Next To</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col xs={1}>
                    <Button
                      variant="danger"
                      onClick={() => removeFieldRow(rowIndex, fieldIndex)}
                    >
                      Remove
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button variant="primary" onClick={() => addFieldRow(rowIndex)} className="me-2">
                Add Another Field
              </Button>
              <Button variant="danger" onClick={() => removeRow(rowIndex)}>
                Remove Row
              </Button>
            </div>
          ))}

          <Button variant="secondary" className="mt-3" onClick={addNewRow}>
            Add New Row
          </Button>

          
        </Form>

        <Row className="mt-4">
          <Col>
            <Button variant="success" onClick={handleGenerateCode}>
              Generate Code
            </Button>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            <Form.Group>
              <Form.Label>Generated Code</Form.Label>
              <InputGroup>
                <Form.Control
                  as="textarea"
                  rows={20}
                  value={generatedCode}
                  readOnly
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => handleCopyToClipboard(generatedCode)}
                >
                  Copy
                </Button>
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            <Form.Group>
              <Form.Label>Generated Dropdown Options Code</Form.Label>
              <InputGroup>
                <Form.Control
                  as="textarea"
                  rows={8}
                  value={generatedDropdownOptions}
                  readOnly
                />
                <Button
                  variant="outline-secondary"
                  onClick={() =>
                    handleCopyToClipboard(generatedDropdownOptions)
                  }
                >
                  Copy
                </Button>
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default FormFieldBuilder;
